import React, {
  useContext, useMemo, useState,
} from 'react';
import { useHistory, useParams } from 'react-router';
import { useIntl } from 'react-intl';
import CustomBtn from '../../../../generic/CustomBtn/CustomBtn';
import DownloadZone from '../../../../components/DownloadZone';
import StageTimeline from '../StageTimeline/StageTimeline';
import StageProgress from '../StageProgress/StageProgress';
import ProjectInfo from '../ProjectInfo/ProjectInfo';
import SubzoneModal from '../SubzoneModal/SubzoneModal';

import pauseImg from '../../../../assets/image/existing-projects/pause-square-small.svg';

import getConfig from '../../../../config';
import { contractMethods, getContract, getTxOutcome } from '../../../../utils/near-utils';
import { ACTIVE_STATUS, PAUSED_STATUS } from '../../../../constans/constans';
import { appStore } from '../../../../state/app';
import { GAS } from '../../../../state/near';
import { getMapping } from '../../../../utils/runtime-mappings';
import { queueToastifyMessage } from '../../../../components/Toastify/Toastify';
import MapMini from '../../../../components/Map/components/MapMini/MapMini';

const { deposit } = getConfig();

const calculateRemaining = (stagesLength, currentStage) => {
  if (!stagesLength) return -1;
  return currentStage === -1 ? -1 : stagesLength - currentStage - 1;
};

const ProjectView = ({
  data,
  runtimeFields,
  stageProgress,
  stageProgressHandler,
  icon,
  filesList,
  isProjectFinished,
}) => {
  const { state } = useContext(appStore);
  const { account } = state;
  const history = useHistory();
  const { nameId } = useParams();
  const translate = useIntl();

  const [projectStatus] = useState(data?.status?.toLowerCase());
  const [stages] = useState(data?.stages);
  const [remainingUpload] = useState(calculateRemaining(stages?.length, getMapping(runtimeFields.currentStage)));
  const [modalType, setModalType] = useState('');

  const pauseProject = async () => {
    try {
      const contract = getContract(account, contractMethods, 0);
      const txResult = await contract.update_project_status({ project_id: nameId, project_status: PAUSED_STATUS }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'update_project_status') {
        throw new Error('update_project_status tx was not successful');
      }
      setModalType('');
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      window.location.reload();
    } catch (e) {
      console.log(e);
    }
  };

  const resumeProject = async () => {
    try {
      const contract = getContract(account, contractMethods, 0);
      const txResult = await contract.update_project_status({ project_id: nameId, project_status: ACTIVE_STATUS }, GAS, deposit);
      const { isSuccess, methodName } = getTxOutcome(txResult);
      if (!isSuccess || methodName !== 'update_project_status') {
        throw new Error('update_project_status tx was not successful');
      }
      setModalType('');
      queueToastifyMessage(translate.formatMessage({ id: 'smart-contract-notify' }));
      window.location.reload();
    } catch (e) {
      console.log(e);
    }
  };

  const modalInfo = useMemo(() => {
    switch (modalType) {
      case 'pause':
        return {
          closeHandler: () => setModalType(''),
          submitHandler: pauseProject,
          closeBtnText: translate.formatMessage({ id: 'button-cancel' }),
          submitBtnText: translate.formatMessage({ id: 'projectView.button-pause' }),
          modalText: translate.formatMessage({ id: 'projectView.modal1-text' }),
          modalTitle: translate.formatMessage({ id: 'projectView.modal1-title' }),
        };
      case 'resume':
        return {
          closeHandler: () => setModalType(''),
          submitHandler: resumeProject,
          closeBtnText: translate.formatMessage({ id: 'button-cancel' }),
          submitBtnText: translate.formatMessage({ id: 'projectView.button-resume' }),
          modalText: translate.formatMessage({ id: 'projectView.modal2-text' }),
          modalTitle: translate.formatMessage({ id: 'projectView.modal2-title' }),
        };
      default:
        return {};
    }
  }, [modalType]);

  return (
    <>
      {!!Object.keys(modalInfo).length && (
        <SubzoneModal {...modalInfo} />
      )}
      {!!stages?.length && (
        <div className="project__stage">
          <StageTimeline stages={stages} runtimeFields={runtimeFields} />
        </div>
      )}
      {stageProgress >= 0 && (
        <div className="project__progress">
          <StageProgress
            stageProgress={stageProgress}
            stageProgressHandler={stageProgressHandler}
          />
        </div>
      )}
      <div className="project__border">
        <div className="project__info">
          <ProjectInfo
            data={data}
            remainingUpload={remainingUpload}
            icon={icon}
            filesList={filesList}
          />
        </div>
        <div className="project__location">
          {data.region && (
            <span className="location-title">
              {translate.formatMessage({ id: 'projectView.location' })}
              :
              {' '}
              {data.region}
            </span>
          )}
          <div className="project__download">
            <DownloadZone
              customClass=""
              showPosition="bottom"
              fileName={`ofp_${data.name}_nftZone`}
              coordinate={data}
              buttonText={translate.formatMessage({ id: 'download.zone' })}
              downloadAll="all"
            />
          </div>
          <div className="location-img">
            <MapMini
              state={data}
            />
          </div>
        </div>
      </div>

      {!isProjectFinished && (
        <div className="project__footer">
          {projectStatus === ACTIVE_STATUS
            && (
            <CustomBtn
              label={translate.formatMessage({ id: 'projectView.button-pause' })}
              customClass="btn__edit-primary btn__pause-project"
              iconSvg={pauseImg}
              type="button"
              handleClick={() => setModalType('pause')}
            />
            )}
          {projectStatus === PAUSED_STATUS
            && (
            <CustomBtn
              label={translate.formatMessage({ id: 'projectView.button-resume' })}
              customClass="btn__edit-primary btn__pause-project"
              iconClass="icon-play-circle"
              type="button"
              handleClick={() => setModalType('resume')}
            />
            )}
          <CustomBtn
            label={translate.formatMessage({ id: 'projectView.stateEdit' })}
            customClass="btn__edit-primary"
            iconClass="icon-pencil-alt"
            type="button"
            handleClick={() => history.push({ pathname: `/edit/${nameId}`, state: data })}
          />
        </div>
      )}
    </>
  );
};

export default ProjectView;
